import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Cloud,
  Plus,
  Trash2,
  Settings,
  CheckCircle2,
  XCircle,
  Key,
  Globe,
  Building,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Edit2,
  Eye,
  EyeOff,
  Activity,
  Database,
  Server,
  TrendingUp,
  Bot,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useCloudConfig } from '../../context/CloudConfigContext';
import { useAuth } from '../../context/AuthContext';
import CloudConfigOnboarding, {
  shouldShowOnboarding,
} from '../../components/cloud-config/CloudConfigOnboarding';
import { toast } from 'sonner';

import { API } from '../../lib/api';
import { providerConfig } from './cloud-config/providerConfig';
import AwsCurSection from './cloud-config/AwsCurSection';


export const CloudConfig = () => {
  const { fetchConfig: refreshGlobalConfig } = useCloudConfig();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  // Per-row testing state: Set<accountId> so each Test Connection runs
  // independently and only the button being clicked shows a spinner.
  const [testingIds, setTestingIds] = useState(new Set());
  // Onboarding modal — fires once for first-time users.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // AWS CUR draft collected during Add Account; persisted after the account is created.
  const [curDraft, setCurDraft] = useState(null);
  
  // Log Sources state
  const [logSources, setLogSources] = useState([]);
  const [logSourcesLoading, setLogSourcesLoading] = useState(false);
  const [logSourceConfigDialog, setLogSourceConfigDialog] = useState(null);
  const [logSourceConfig, setLogSourceConfig] = useState({});
  const [savingLogSourceConfig, setSavingLogSourceConfig] = useState(false);

  // Log source configuration fields
  const logSourceConfigFields = {
    azure_monitor: [
      { key: 'tenant_id', label: 'Azure Tenant ID', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_id', label: 'Azure Client ID (App ID)', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_secret', label: 'Azure Client Secret', type: 'password', placeholder: 'Enter client secret' },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' }
    ],
    splunk: [
      { key: 'base_url', label: 'Splunk Base URL', type: 'text', placeholder: 'https://your-splunk.com:8089' },
      { key: 'token', label: 'Splunk Token', type: 'password', placeholder: 'Enter Splunk token' }
    ],
    newrelic: [
      { key: 'api_key', label: 'New Relic API Key', type: 'password', placeholder: 'NRAK-...' },
      { key: 'account_id', label: 'Account ID', type: 'text', placeholder: '1234567' }
    ],
    datadog: [
      { key: 'api_key', label: 'Datadog API Key', type: 'password', placeholder: 'Enter API key' },
      { key: 'app_key', label: 'Datadog App Key', type: 'password', placeholder: 'Enter App key' }
    ],
    grafana: [
      { key: 'url', label: 'Grafana/Loki URL', type: 'text', placeholder: 'https://your-grafana.com' },
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter API key' }
    ]
  };

  useEffect(() => {
    fetchConfig();
    fetchLogSources();
  }, []);

  // Onboarding tour: fire on first visit IF (a) the user hasn't dismissed
  // it AND (b) they have no cloud accounts connected yet. We deliberately
  // don't fire it for users who already have an account — they don't need
  // a tour for something they've already done.
  useEffect(() => {
    if (loading) return;
    if (!user?.user_id) return;
    if (!shouldShowOnboarding(user.user_id)) return;
    const totalAccounts =
      (config?.accounts?.aws?.length || 0) +
      (config?.accounts?.azure?.length || 0) +
      (config?.accounts?.gcp?.length || 0);
    if (totalAccounts === 0) {
      setOnboardingOpen(true);
    }
  }, [loading, user?.user_id, config]);

  const fetchLogSources = async () => {
    setLogSourcesLoading(true);
    try {
      const response = await axios.get(`${API}/sre-agent/sources`);
      setLogSources(response.data.sources || []);
    } catch (error) {
      console.error('Error fetching log sources:', error);
    } finally {
      setLogSourcesLoading(false);
    }
  };

  const toggleLogSource = async (source, enabled) => {
    try {
      await axios.put(`${API}/sre-agent/sources/${source}/toggle?enabled=${enabled}`);
      setLogSources(prev => 
        prev.map(s => s.source === source ? { ...s, enabled } : s)
      );
      toast.success(`${source} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update log source');
    }
  };

  const openLogSourceConfig = (source) => {
    setLogSourceConfigDialog(source);
    setLogSourceConfig({});
  };

  const saveLogSourceConfig = async () => {
    if (!logSourceConfigDialog) return;
    
    setSavingLogSourceConfig(true);
    try {
      await axios.put(`${API}/sre-agent/sources/${logSourceConfigDialog}/config`, logSourceConfig);
      toast.success(`${logSourceConfigDialog} configured successfully`);
      setLogSourceConfigDialog(null);
      setLogSourceConfig({});
      fetchLogSources();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingLogSourceConfig(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const [configResponse, accountsResponse] = await Promise.all([
        axios.get(`${API}/cloud/config`, { withCredentials: true }),
        axios.get(`${API}/cloud/accounts`, { withCredentials: true }),
      ]);
      
      setConfig({
        enabled_providers: configResponse.data.enabled_providers || [],
        accounts: accountsResponse.data.by_provider || { aws: [], azure: [], gcp: [] }
      });
    } catch (error) {
      console.error('Error fetching cloud config:', error);
      // Initialize with empty config
      setConfig({
        enabled_providers: [],
        accounts: { aws: [], azure: [], gcp: [] }
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleProvider = async (provider) => {
    try {
      const isCurrentlyEnabled = config.enabled_providers.includes(provider);
      const newEnabledProviders = isCurrentlyEnabled 
        ? config.enabled_providers.filter(p => p !== provider)
        : [...config.enabled_providers, provider];
      
      await axios.post(
        `${API}/cloud/config`,
        { enabled_providers: newEnabledProviders },
        { withCredentials: true },
      );
      
      setConfig(prev => ({
        ...prev,
        enabled_providers: newEnabledProviders
      }));
      
      // Refresh global context so other pages update
      refreshGlobalConfig();
      
      toast.success(`${providerConfig[provider].name} ${isCurrentlyEnabled ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update provider status');
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────
  // Build the {provider, name, account_id, credentials} payload that
  // POST /cloud/accounts expects. Reads the per-provider credentialFields
  // map to know which keys belong to credentials (the rest stays at the
  // top level of the payload).
  const buildAccountPayload = (provider, formState, fallbackName = '', fallbackAccountId = '') => {
    const credentials = {};
    const credFields = providerConfig[provider]?.credentialFields || [];
    credFields.forEach((field) => {
      if (formState[field.key]) credentials[field.key] = formState[field.key];
    });
    return {
      provider,
      name: formState.name || fallbackName,
      account_id: formState.account_id || fallbackAccountId,
      credentials,
    };
  };

  // The backend may return `detail` as either a string (legacy) or an
  // object ({message, reason, provider}) for invalid_credentials cases.
  // Always extract a human-readable message for the toast — never let
  // the user see a generic fallback when the backend actually told us
  // why the call failed.
  const extractErrorMessage = (error, fallback) => {
    // 1. Axios got a response → parse the FastAPI `detail` payload
    if (error?.response) {
      const data = error.response.data;
      const detail = data?.detail;
      if (typeof detail === 'string' && detail.length) return detail;
      if (detail?.message) return detail.message;
      // Pydantic 422 — `detail` is an array of {loc, msg, type}
      if (Array.isArray(detail) && detail.length) {
        return detail.map((d) => d.msg || d.message || JSON.stringify(d)).join('; ');
      }
      // Some FastAPI paths return {message: "..."} at the top level
      if (typeof data?.message === 'string') return data.message;
      // Last-ditch: surface the HTTP status so the user has SOMETHING actionable
      return `${fallback || 'Request failed'} (HTTP ${error.response.status})`;
    }
    // 2. Network / CORS / timeout — no response object
    if (error?.request) {
      return `${fallback || 'Request failed'} — backend unreachable (network error)`;
    }
    // 3. Something blew up before axios could send (validation, etc.)
    if (error?.message) return error.message;
    return fallback || 'Request failed';
  };

  const openAddAccount = (provider) => {
    setSelectedProvider(provider);
    setNewAccount({ provider, name: '', enabled: true });
    setCurDraft(null);
    setAddDialogOpen(true);
  };

  const addAccount = async () => {
    try {
      const payload = buildAccountPayload(selectedProvider, newAccount);
      const response = await axios.post(
        `${API}/cloud/accounts`, payload, { withCredentials: true },
      );

      setConfig((prev) => ({
        ...prev,
        enabled_providers: prev.enabled_providers.includes(selectedProvider)
          ? prev.enabled_providers
          : [...prev.enabled_providers, selectedProvider],
        accounts: {
          ...prev.accounts,
          [selectedProvider]: [...(prev.accounts[selectedProvider] || []), response.data.account],
        },
      }));

      // If the user filled CUR fields during Add, persist them now that we
      // have an account ID. Best-effort — failure is non-fatal.
      const newAccId = response.data.account?.id;
      if (selectedProvider === 'aws' && newAccId && curDraft && curDraft.enabled) {
        try {
          await axios.put(
            `${API}/cloud/accounts/${newAccId}/cur`,
            curDraft,
            { withCredentials: true },
          );
          toast.success('AWS CUR configuration saved');
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Account created but failed to save CUR config — edit the account to retry.');
        }
      }

      refreshGlobalConfig();
      const verifiedId = response.data.account?.account_id;
      toast.success(verifiedId
        ? `Connected to ${verifiedId} — credentials verified`
        : 'Cloud account added');
      setAddDialogOpen(false);
      setNewAccount({});
      setCurDraft(null);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to add cloud account'));
    }
  };

  const editAccount = async () => {
    if (!selectedAccount || !selectedProvider) return;
    try {
      const payload = buildAccountPayload(
        selectedProvider, newAccount,
        selectedAccount.name, selectedAccount.account_id,
      );

      // Save-then-delete (NOT the other way around). The backend validates
      // creds BEFORE persisting, so if validation fails the user keeps their
      // existing account intact and sees a clear error. We only remove the
      // old account once the new one is in place.
      const create = await axios.post(
        `${API}/cloud/accounts`, payload, { withCredentials: true },
      );
      await axios.delete(
        `${API}/cloud/accounts/${selectedAccount.id}`,
        { withCredentials: true },
      ).catch(() => {/* best-effort — new account already saved */});

      refreshGlobalConfig();
      const verifiedId = create.data.account?.account_id;
      toast.success(verifiedId
        ? `Updated — connected to ${verifiedId}`
        : 'Account credentials updated');
      setEditDialogOpen(false);
      setNewAccount({});
      fetchConfig();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update account'));
    }
  };

  const deleteAccount = async () => {
    if (!selectedAccount) return;
    try {
      await axios.delete(
        `${API}/cloud/accounts/${selectedAccount.id}`,
        { withCredentials: true },
      );
      setConfig(prev => ({
        ...prev,
        accounts: {
          ...prev.accounts,
          [selectedAccount.provider]: prev.accounts[selectedAccount.provider].filter(a => a.id !== selectedAccount.id)
        }
      }));
      toast.success('Cloud account removed');
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const testConnection = async (account) => {
    setTestingIds((prev) => new Set(prev).add(account.id));
    try {
      const response = await axios.post(
        `${API}/cloud/accounts/${account.id}/test`,
        {},
        { withCredentials: true },
      );
      if (response.data.connected) {
        const detail = response.data.detail || {};
        const verified =
          detail.account_id || detail.subscription_id || detail.project_id || account.name;
        toast.success(`Connected to ${verified}`);
        // Refresh list so the row's status badge updates from any prior error.
        fetchConfig();
      } else {
        toast.error(`Connection failed: ${response.data.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Connection test failed'));
    } finally {
      setTestingIds((prev) => {
        const next = new Set(prev);
        next.delete(account.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const enabledCount = config?.enabled_providers?.length || 0;
  const totalAccounts = Object.values(config?.accounts || {}).flat().length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cloud-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Cloud className="w-7 h-7 text-primary" />
            Cloud Configuration
          </h2>
          <p className="text-muted-foreground mt-1">
            Connect and manage your cloud provider accounts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-primary border-primary/30">
            {enabledCount} Providers Enabled
          </Badge>
          <Badge variant="secondary">
            {totalAccounts} Accounts Connected
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOnboardingOpen(true)}
            data-testid="open-setup-guide-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-cyan-400" />
            Setup guide
          </Button>
        </div>
      </div>

      {/* Onboarding modal — fires automatically on first visit, re-openable
          via the "Setup guide" button above. */}
      <CloudConfigOnboarding
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />

      {/* Quick Stats */}
      {enabledCount === 0 && (
        <Alert className="border-yellow-500/30 bg-yellow-500/5">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <AlertDescription className="text-yellow-500">
            No cloud providers enabled. Enable at least one provider to start monitoring your infrastructure.
          </AlertDescription>
        </Alert>
      )}

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(providerConfig).map(([providerId, provider]) => {
          const isEnabled = config?.enabled_providers?.includes(providerId);
          const accounts = config?.accounts?.[providerId] || [];
          
          return (
            <Card 
              key={providerId} 
              className={`bg-card border-2 transition-all ${isEnabled ? provider.borderColor : 'border-border'}`}
              data-testid={`provider-card-${providerId}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{provider.logo}</span>
                    <div>
                      <CardTitle className="text-lg">{provider.name}</CardTitle>
                      <CardDescription>{accounts.length} accounts</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => toggleProvider(providerId)}
                    data-testid={`toggle-${providerId}`}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* Always show accounts and add button */}
                <>
                  {/* Accounts List */}
                  <div className="space-y-2 mb-4">
                    {accounts.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No accounts connected yet
                      </p>
                    ) : (
                      accounts.map((account) => (
                        <div 
                          key={account.id} 
                          className={`p-3 rounded-lg ${provider.bgColor} flex items-center justify-between`}
                        >
                          <div className="flex items-center gap-2">
                            {account.status === 'connected' ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                            <div>
                              <p className="font-medium text-sm">{account.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {account.resources > 0 
                                  ? `${account.resources} resources | $${account.monthly_cost?.toFixed(2) || '0'}/mo` 
                                  : account.account_id || account.subscription_id || account.region || 'Connected'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedProvider(providerId);
                                setSelectedAccount(account);
                                setNewAccount({ ...account, provider: providerId });
                                setEditDialogOpen(true);
                              }}
                              data-testid={`edit-${account.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-primary" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => testConnection(account)}
                              disabled={testingIds.has(account.id)}
                              data-testid={`test-connection-${account.id}`}
                            >
                              <RefreshCw className={`w-4 h-4 ${testingIds.has(account.id) ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => {
                                setSelectedAccount(account);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => openAddAccount(providerId)}
                    data-testid={`add-account-${providerId}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add {provider.accountLabel}
                  </Button>
                </>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Connected Accounts Summary */}
      {totalAccounts > 0 && (
        <Card className="bg-card border-border" data-testid="accounts-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Connected Cloud Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(config?.accounts || {}).map(([providerId, accounts]) => {
                if (accounts.length === 0) return null;
                const provider = providerConfig[providerId];
                
                return accounts.map((account) => (
                  <div 
                    key={account.id}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{provider.logo}</span>
                      <div>
                        <p className="font-medium">{account.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {account.region || account.subscription_id || provider.accountIdLabel}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant="secondary" 
                        className={account.status === 'connected' ? 'bg-green-500/15 text-green-500' : 'bg-red-500/15 text-red-500'}
                      >
                        {account.status || 'pending'}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {account.resources || account.resources_count || 0} resources
                      </span>
                    </div>
                  </div>
                ));
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Log Sources Configuration */}
      <Card className="bg-card border-border" data-testid="log-sources-config">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            Log & Monitoring Sources
            <Badge variant="outline" className="ml-auto">
              {logSources.filter(s => s.enabled).length} / {logSources.length} Enabled
            </Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Connect log sources so the SRE Agent can investigate incidents end-to-end.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {logSources.map((source) => {
              const sourceIcons = {
                cloudwatch: { icon: <Cloud className="w-5 h-5 text-orange-400" />, name: 'AWS CloudWatch' },
                azure_monitor: { icon: <Cloud className="w-5 h-5 text-blue-400" />, name: 'Azure Monitor' },
                splunk: { icon: <Database className="w-5 h-5 text-green-500" />, name: 'Splunk' },
                newrelic: { icon: <Activity className="w-5 h-5 text-teal-500" />, name: 'New Relic' },
                datadog: { icon: <TrendingUp className="w-5 h-5 text-purple-500" />, name: 'Datadog' },
                grafana: { icon: <Activity className="w-5 h-5 text-orange-400" />, name: 'Grafana Loki' }
              };
              const info = sourceIcons[source.source] || { icon: <Server className="w-5 h-5" />, name: source.source };
              
              return (
                <div 
                  key={source.source}
                  className={`p-4 rounded-lg border-2 min-h-[210px] flex flex-col transition-all ${
                    source.enabled && source.configured 
                      ? 'border-green-500/50 bg-green-500/5' 
                      : source.configured 
                        ? 'border-border' 
                        : 'border-dashed border-muted'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {typeof info.icon === 'string' ? <span className="text-2xl">{info.icon}</span> : info.icon}
                      <div>
                        <p className="font-medium">{info.name}</p>
                        <p className="text-xs text-muted-foreground">{source.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={source.enabled}
                      onCheckedChange={(enabled) => toggleLogSource(source.source, enabled)}
                      disabled={!source.configured && !source.configurable}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {source.configured ? (
                        <Badge className="bg-green-500/15 text-green-500 text-xs">
                          {source.config_source === 'cloud_config' ? 'Uses Cloud Config' : 'Configured'}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Not Configured</Badge>
                      )}
                      {source.enabled && source.configured && (
                        <Badge className="bg-primary/15 text-primary text-xs">Active</Badge>
                      )}
                      
                      {/* Configure button for third-party sources */}
                      {source.configurable && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="ml-auto h-7 text-xs"
                          onClick={() => openLogSourceConfig(source.source)}
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Configure
                        </Button>
                      )}
                    </div>
                    
                    {/* Show info for cloud sources */}
                    {source.source === 'cloudwatch' && !source.configured && (
                      <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs">
                        <p>Configure your AWS credentials above to enable this source.</p>
                      </div>
                    )}
                    
                    {/* Show required env vars for unconfigured third-party sources */}
                    {source.configurable && !source.configured && (
                      <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                        <p className="mb-1">Click <strong>Configure</strong> to set up, or use environment variables:</p>
                        <div className="flex flex-wrap gap-1">
                          {source.env_vars_required?.map((v) => (
                            <code key={v} className="bg-background px-1 rounded">{v}</code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm">
              <strong>Tip:</strong> Click <strong>Configure</strong> on any source after you've connected the matching cloud account above.
              Third-party tools (Splunk, Datadog, New Relic, Grafana Loki) can be wired via the Configure button or environment variables.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Log Source Configuration Dialog */}
      <Dialog open={!!logSourceConfigDialog} onOpenChange={() => setLogSourceConfigDialog(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configure {logSourceConfigDialog?.replace('_', ' ')}
            </DialogTitle>
            <DialogDescription>
              Enter credentials to connect to this monitoring service
            </DialogDescription>
          </DialogHeader>
          
          {logSourceConfigDialog && logSourceConfigFields[logSourceConfigDialog] && (
            <div className="space-y-4 py-4">
              {logSourceConfigFields[logSourceConfigDialog].map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    type={field.type}
                    value={logSourceConfig[field.key] || ''}
                    onChange={(e) => setLogSourceConfig(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="bg-secondary border-border"
                  />
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogSourceConfigDialog(null)}>
              Cancel
            </Button>
            <Button onClick={saveLogSourceConfig} disabled={savingLogSourceConfig}>
              {savingLogSourceConfig ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save & Enable
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Account Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedProvider && (
                <>
                  <span className="text-xl">{providerConfig[selectedProvider]?.logo}</span>
                  Add {providerConfig[selectedProvider]?.accountLabel}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Enter your cloud credentials to connect this account
            </DialogDescription>
          </DialogHeader>
          
          {selectedProvider && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="account-name">Account Name</Label>
                <Input
                  id="account-name"
                  value={newAccount.name || ''}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Production Account"
                  className="bg-secondary border-border"
                  data-testid="account-name-input"
                />
              </div>

              {providerConfig[selectedProvider]?.credentialFields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  {field.type === 'select' ? (
                    <Select
                      value={newAccount[field.key] || ''}
                      onValueChange={(value) => setNewAccount(prev => ({ ...prev, [field.key]: value }))}
                    >
                      <SelectTrigger className="bg-secondary border-border" data-testid={`cred-field-${field.key}`}>
                        <SelectValue placeholder={`Select ${field.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options?.map((opt) => (
                          <SelectItem key={opt} value={opt} data-testid={`cred-field-${field.key}-option-${opt}`}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      id={field.key}
                      value={newAccount[field.key] || ''}
                      onChange={(e) => setNewAccount(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="w-full h-24 p-3 bg-secondary border border-border rounded-md text-sm font-mono"
                      data-testid={`cred-field-${field.key}`}
                    />
                  ) : (
                    <div className="relative">
                      <Input
                        id={field.key}
                        type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                        value={newAccount[field.key] || ''}
                        onChange={(e) => setNewAccount(prev => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        className="bg-secondary border-border pr-10"
                        data-testid={`cred-field-${field.key}`}
                      />
                      {field.type === 'password' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                          onClick={() => setShowSecrets(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                        >
                          {showSecrets[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* AWS-only — optional CUR section inside the Add Account flow.
              Deferred mode: the parent stores the draft and persists it after
              the AWS account is successfully created. */}
          {selectedProvider === 'aws' && (
            <div className="px-1 pb-1">
              <AwsCurSection
                deferred
                value={curDraft}
                onChange={setCurDraft}
                defaultRegion={newAccount.region}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={addAccount} disabled={!newAccount.name} data-testid="save-account-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Connect Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Remove Cloud Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{selectedAccount?.name}"? This will stop monitoring all resources in this account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteAccount} data-testid="confirm-delete-account">
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Account Credentials */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" />
              Update Credentials — {selectedAccount?.name}
            </DialogTitle>
            <DialogDescription>
              Update credentials for this {providerConfig[selectedProvider]?.name} account. This will override the existing configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Account Name</Label>
              <Input
                value={newAccount.name || ''}
                onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                className="bg-secondary border-border mt-1"
              />
            </div>
            {selectedProvider && providerConfig[selectedProvider]?.credentialFields.map((field) => (
              <div key={field.key}>
                <Label className="text-sm">{field.label}</Label>
                {field.type === 'select' ? (
                  <Select value={newAccount[field.key] || ''} onValueChange={(v) => setNewAccount(prev => ({ ...prev, [field.key]: v }))}>
                    <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue placeholder={field.placeholder} /></SelectTrigger>
                    <SelectContent>{field.options?.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={field.type === 'password' ? (showSecrets[field.key] ? 'text' : 'password') : 'text'}
                    placeholder={field.placeholder}
                    value={newAccount[field.key] || ''}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-secondary border-border mt-1"
                  />
                )}
              </div>
            ))}

            {/* AWS-only: optional CUR (Cost & Usage Reports) configuration.
                User decides at AWS-account configure time whether to enable
                CUR (free Athena reads) or stay on Cost Explorer ($). */}
            {selectedProvider === 'aws' && selectedAccount?.id && (
              <AwsCurSection
                accountId={selectedAccount.id}
                initialConfig={selectedAccount.cur_config}
                defaultRegion={newAccount.region || selectedAccount.region}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={editAccount} data-testid="save-edit-account">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Update Credentials
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CloudConfig;
